import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { SlashCommandItem, SlashCommandGroup } from "./slashMenuList";
import { Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Editor } from "@tiptap/core";
// Command List Component - This is the popup menu that shows the commands
export interface CommandListRef {
  onKeyDown: (event: KeyboardEvent) => boolean;
}

export interface CommandListProps {
  items: SlashCommandGroup[];
  fixedItems: SlashCommandItem[];
  command: (item: SlashCommandItem) => void;
  editor: Editor;
}

export const CommandList = forwardRef<CommandListRef, CommandListProps>(
  (props, ref) => {
    const theme = useTheme();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    // Flatten all items for keyboard navigation
    const flattenedItems = useMemo(() => {
      const items: Array<{
        item: SlashCommandItem;
        type: "fixed" | "nested";
        parentIndex: number;
        childIndex: number;
      }> = [];

      // Add fixed items
      props.fixedItems.forEach((item, index) => {
        items.push({
          item,
          type: "fixed",
          parentIndex: index,
          childIndex: 0,
        });
      });

      // Add nested items
      props.items.forEach((group, groupIndex) => {
        group.items.forEach((item, itemIndex) => {
          items.push({
            item,
            type: "nested",
            parentIndex: groupIndex,
            childIndex: itemIndex,
          });
        });
      });

      return items;
    }, [props.fixedItems, props.items]);

    const selectItem = useCallback(
      ({
        parentIndex,
        childIndex,
        type = "nested",
      }: {
        parentIndex: number;
        childIndex: number;
        type?: "fixed" | "nested";
      }) => {
        const item =
          type === "fixed"
            ? props.fixedItems[parentIndex]
            : props.items[parentIndex].items[childIndex];
        if (item) {
          props.command(item);
        }
      },
      [props],
    );

    const selectCurrentItem = useCallback(() => {
      const currentItem = flattenedItems[selectedIndex];
      if (currentItem) {
        selectItem({
          parentIndex: currentItem.parentIndex,
          childIndex: currentItem.childIndex,
          type: currentItem.type,
        });
      }
    }, [selectedIndex, flattenedItems, selectItem]);

    const upHandler = useCallback(() => {
      setSelectedIndex(
        (selectedIndex + flattenedItems.length - 1) % flattenedItems.length,
      );
    }, [selectedIndex, flattenedItems.length]);

    const downHandler = useCallback(() => {
      setSelectedIndex((selectedIndex + 1) % flattenedItems.length);
    }, [selectedIndex, flattenedItems.length]);

    const enterHandler = useCallback(() => {
      selectCurrentItem();
    }, [selectCurrentItem]);

    useEffect(() => setSelectedIndex(0), [props.items]);

    // Scroll selected item into view
    useEffect(() => {
      const selectedElement = itemRefs.current[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, [selectedIndex]);

    useImperativeHandle(ref, () => ({
      onKeyDown: (event: KeyboardEvent) => {
        if (event.key === "ArrowUp") {
          upHandler();
          return true;
        }

        if (event.key === "ArrowDown") {
          downHandler();
          return true;
        }

        if (event.key === "Enter") {
          enterHandler();
          return true;
        }

        return false;
      },
    }));

    return (
      <Stack
        sx={{
          boxShadow: "-4px 9px 11px 0px #00000014",
          border: `1px solid ${theme.palette.neutral["020"]}`,
          borderRadius: "8px",
          maxHeight: "400px",
          width: "320px",
          overflowY: "auto",
          backgroundColor: "#FFF",
        }}
      >
        <Stack
          direction={"row"}
          sx={{
            m: "8px",
            p: "4px 8px",
            border: `1px solid ${theme.palette.neutral["020"]}`,
            borderRadius: "8px",
            boxShadow: "0px 0px 4px 0px #0000000F,0px 0px 2px 0px #0000000A",
            gap: "4px",
          }}
        >
          {props?.fixedItems?.map((item, index) => {
            const flatIndex = flattenedItems.findIndex(
              (flatItem) =>
                flatItem.type === "fixed" && flatItem.parentIndex === index,
            );
            const isSelected = selectedIndex === flatIndex;

            return (
              <Stack
                direction="row"
                key={index}
                // @ts-ignore
                ref={(el) => (itemRefs.current[flatIndex] = el)}
                sx={{
                  cursor: "pointer",
                  border: "none",
                  backgroundColor: isSelected
                    ? theme.palette.neutral["B-005"]
                    : item.isActive({ editor: props.editor })
                      ? theme.palette.neutral["005"]
                      : "transparent",

                  borderRadius: "6px",
                  padding: "4px",
                  outline: "none",
                  alignItems: "center",
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["010"],
                  },
                }}
                onClick={() =>
                  selectItem({
                    parentIndex: index,
                    childIndex: 0,
                    type: "fixed",
                  })
                }
                component="button"
              >
                {item.icon}
              </Stack>
            );
          })}
        </Stack>
        {props.items.length ? (
          props.items.map((item, index) => (
            <>
              <Stack sx={{ p: "8px 16px" }}>
                <Typography
                  variant="body-small"
                  color={theme.palette.lightBg.low}
                >
                  {item.title}
                </Typography>
              </Stack>

              {item.items.map((childItem, childIndex) => {
                const flatIndex = flattenedItems.findIndex(
                  (flatItem) =>
                    flatItem.type === "nested" &&
                    flatItem.parentIndex === index &&
                    flatItem.childIndex === childIndex,
                );
                const isSelected = selectedIndex === flatIndex;

                return (
                  <Stack
                    direction="row"
                    key={childIndex}
                    // @ts-ignore
                    ref={(el) => (itemRefs.current[flatIndex] = el)}
                    sx={{
                      p: "12px 16px",
                      cursor: "pointer",
                      border: "none",
                      backgroundColor: isSelected
                        ? theme.palette.neutral["B-005"]
                        : childItem.isActive({
                              editor: props.editor,
                            })
                          ? theme.palette.neutral["005"]
                          : "transparent",

                      outline: "none",
                      alignItems: "center",
                      gap: "8px",
                      "&:hover": {
                        backgroundColor: theme.palette.neutral["010"],
                      },
                    }}
                    onClick={() =>
                      selectItem({
                        parentIndex: index,
                        childIndex: childIndex,
                        type: "nested",
                      })
                    }
                    component="button"
                  >
                    {childItem.icon}

                    <Typography variant="body-medium">
                      {childItem.title}
                    </Typography>
                  </Stack>
                );
              })}
            </>
          ))
        ) : (
          <div style={menuStyles.noResults}>No results</div>
        )}
      </Stack>
    );
  },
);

CommandList.displayName = "CommandList";

const menuStyles = {
  container: {
    background: "white",
    borderRadius: "8px",
    boxShadow:
      "0 10px 38px -10px rgba(22, 23, 24, 0.35), 0 10px 20px -15px rgba(22, 23, 24, 0.2)",
    fontSize: "14px",
    maxHeight: "400px",
    width: "320px",
    overflowY: "auto" as const,
    border: "1px solid #e5e7eb",
    padding: "4px",
  },
  item: {
    alignItems: "flex-start",
    background: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    display: "flex",
    gap: "12px",
    outline: "none",
    padding: "12px",
    textAlign: "left" as const,
    width: "100%",
    transition: "background-color 0.1s ease",
  },
  selectedItem: {
    backgroundColor: "#f3f4f6",
  },
  iconContainer: {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    height: "24px",
    justifyContent: "center",
    width: "24px",
  },
  icon: {
    fontSize: "16px",
  },
  content: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontWeight: "500",
    lineHeight: "1.2",
    marginBottom: "2px",
    color: "#111827",
  },
  description: {
    color: "#6b7280",
    fontSize: "12px",
    lineHeight: "1.3",
  },
  noResults: {
    color: "#9ca3af",
    fontSize: "12px",
    padding: "12px 16px",
    textAlign: "center" as const,
  },
};
